import { useEffect, useRef, useState } from 'react';
import type { Address } from 'viem';
import type { LockDetails } from './useAdmin';

/** Cache only within a data revision; serialize page reads even during rapid navigation. */
export function useGrantPages({ addresses, revision, selectedAddress, load }: {
  addresses: readonly string[];
  revision: string;
  selectedAddress: string | null;
  load: (addresses: readonly Address[], signal?: AbortSignal) => Promise<Record<string, LockDetails>>;
}) {
  const [requestedPage, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [attempt, retry] = useState(0);
  const [snapshot, setSnapshot] = useState({ revision, details: {} as Record<string, LockDetails> });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cache = useRef(snapshot);
  const queue = useRef(Promise.resolve());
  const loader = useRef(load);
  loader.current = load;

  const pageCount = Math.max(1, Math.ceil(addresses.length / pageSize));
  const selectedIndex = addresses.findIndex((address) => address.toLowerCase() === selectedAddress?.toLowerCase());
  const page = selectedIndex >= 0
    ? Math.floor(selectedIndex / pageSize)
    : Math.min(requestedPage, pageCount - 1);
  // Retain the selected grant's page when its details are collapsed.
  useEffect(() => setPage(page), [page]);
  const pageAddresses = addresses.slice(page * pageSize, (page + 1) * pageSize);
  const pageKey = pageAddresses.join(',');
  const details = snapshot.revision === revision ? snapshot.details : {};

  useEffect(() => {
    const controller = new AbortController();
    if (cache.current.revision !== revision) {
      cache.current = { revision, details: {} };
      setSnapshot(cache.current);
    }
    const missing = pageAddresses.filter((address) => !cache.current.details[address]);
    setError(null);
    setLoading(missing.length > 0);
    if (missing.length === 0) return () => controller.abort();

    queue.current = queue.current.then(async () => {
      if (controller.signal.aborted) return;
      try {
        const fetched = await loader.current(missing as Address[], controller.signal);
        if (controller.signal.aborted) return;
        cache.current = { revision, details: { ...cache.current.details, ...fetched } };
        setSnapshot(cache.current);
        if (missing.some((address) => !fetched[address])) {
          setError('Some grants could not be loaded. Retry to load the missing details.');
        }
      } catch {
        if (!controller.signal.aborted) setError('Grant details could not be loaded. Please retry.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    });
    return () => controller.abort();
    // pageKey tracks address contents, not a new array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, pageKey, attempt]);

  return { page, pageCount, pageSize, pageAddresses, details, loading, error,
    setPage, setPageSize: (size: number) => { setPage(0); setPageSize(size); },
    retry: () => retry((value) => value + 1) };
}
